import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Typography, TextField, Select, MenuItem, FormControl,
  InputLabel, Button, Snackbar, Alert, Checkbox, ListItemText, OutlinedInput
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { getUserDetails } from '../../../src/storageService';
import {
  postFacultyGoals,
  postStudentGoals,
  getPicklistValuesFaculty,
} from '../../Utils/apiService';

interface SubGoal {
  title: string;
  description: string;
  startDate: Date | null;
  endDate: Date | null;
}

const AddGoalPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userDetails = getUserDetails();
  const isStudent = userDetails?.Type === 'Student';
  const prefill = location.state as {
    id?: string;
    title?: string;
    description?: string;
    type?: string;
    goaltype?: string;
    goalType?: string;
    startdate?: string;
    enddate?: string;
    applicableTo?: string;
    audienceNameforfac?: string;
    subgoalsname?: string;
    subgoalsdescription?: string;
    subgoalsstartdate?: string;
    subgoalsenddate?: string;
    subgoals?: { title?: string; description?: string; startDate?: string; endDate?: string }[];
  } | undefined;
  

  const [name, setName] = useState(prefill?.title || '');
  const [description, setDescription] = useState(prefill?.description || '');
  const [startDate, setStartDate] = useState<Date | null>(
    prefill?.startdate ? new Date(prefill.startdate) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    prefill?.enddate ? new Date(prefill.enddate) : null
  );
  const [goaltype, setGoalType] = useState(prefill?.goalType || prefill?.type || '');
  const [applicableTo, setApplicableTo] = useState(prefill?.applicableTo || '');
  const [audienceName, setAudienceName] = useState<string[]>([]);
  const [fileName, setFileName] = useState('');
  const [base64File, setBase64File] = useState('');
  const [searchText, setSearchText] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [picklistData, setPicklistData] = useState<Record<string, Record<string, string>>>({});
  const [audienceOptions, setAudienceOptions] = useState<{ label: string; value: string }[]>([]);
  const prefilledAudienceIds = prefill?.audienceNameforfac
  ?.split(',')
  .map(id => id.trim()) || [];


  const [step, setStep] = useState<1 | 2>(1);
  const [subGoals, setSubGoals] = useState<SubGoal[]>(() => {
    if (prefill?.subgoals && Array.isArray(prefill.subgoals) && prefill.subgoals.length > 0) {
      console.log("Received subgoals from location.state:", prefill.subgoals);
      return prefill.subgoals.map((sg: any) => ({
        title: sg.title || '',
        description: sg.description || '',
        startDate: sg.startDate ? new Date(sg.startDate) : null,
        endDate: sg.endDate ? new Date(sg.endDate) : null,
      }));
    }
    
  
    // fallback to your old string-splitting logic if needed
    if (
      prefill?.subgoalsname ||
      prefill?.subgoalsdescription ||
      prefill?.subgoalsstartdate ||
      prefill?.subgoalsenddate
    ) {
      const names = prefill.subgoalsname?.split(',').map(s => s.trim()).filter(s => s) ?? [];
      const descs = prefill.subgoalsdescription?.split(',').map(s => s.trim()).filter(s => s) ?? [];
      const starts = prefill.subgoalsstartdate?.split(',').map(s => s.trim()).filter(s => s) ?? [];
      const ends = prefill.subgoalsenddate?.split(',').map(s => s.trim()).filter(s => s) ?? [];
  
      const maxLen = Math.max(names.length, descs.length, starts.length, ends.length);
      const result: SubGoal[] = [];
  
      for (let i = 0; i < maxLen; i++) {
        const startDateParsed = starts[i] ? new Date(starts[i]) : null;
        const endDateParsed = ends[i] ? new Date(ends[i]) : null;
  
        const validStartDate = startDateParsed && !isNaN(startDateParsed.getTime()) ? startDateParsed : null;
        const validEndDate = endDateParsed && !isNaN(endDateParsed.getTime()) ? endDateParsed : null;
  
        result.push({
          title: names[i] || '',
          description: descs[i] || '',
          startDate: validStartDate,
          endDate: validEndDate,
        });
      }
  
      return result;
    }
  
    return [{ title: '', description: '', startDate: null, endDate: null }];
  });
  
  
  

  useEffect(() => {
    const fetch = async () => {
      const c = userDetails?.ContactId;
      if (!c) return;
      if (isStudent) {
        setAudienceName([c]);
        return;
      }
      const res = await getPicklistValuesFaculty(c);
      if (res?.success) setPicklistData(res.data);
    };
    fetch();
  }, []);

  useEffect(() => {
    if (
      applicableTo &&
      Object.keys(picklistData).length > 0 &&
      picklistData[applicableTo]
    ) {
      const options = Object.entries(picklistData[applicableTo]).map(([val, lbl]) => ({
        label: lbl,
        value: val,
      }));
      setAudienceOptions(options);
  
      const validPrefilledIds = options
        .filter(option => prefilledAudienceIds.includes(option.value.trim()))
        .map(option => option.value);
  
      setAudienceName(validPrefilledIds);
      setSearchText('');
    }
  }, [applicableTo, picklistData]);
  

  useEffect(() => {
    if (!isStudent && prefill?.applicableTo && Object.keys(picklistData).length > 0) {
      setApplicableTo(prefill.applicableTo); // ✅ This triggers the above useEffect
    }
  }, [picklistData]);
    
  

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onloadend = () => setBase64File((reader.result as string).split(',')[1]);
    reader.readAsDataURL(f);
  };

  const showSnackbar = (msg: string, sev: 'success' | 'error') => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  const fmt = (d: Date | null) => (d ? d.toISOString().split('T')[0] : '');

  const handleSubChange = (i: number, field: keyof SubGoal, val: any) => {
    const arr = [...subGoals];
    arr[i][field] = val;
    setSubGoals(arr);
  };

  const addSub = () => setSubGoals([...subGoals, { title: '', description: '', startDate: null, endDate: null }]);

  const handleSubmit = async () => {
    if (!name || !description || !startDate || !endDate) {
      return showSnackbar('Please fill all required fields', 'error');
    }
    const payload: any = {
      facultyID: userDetails.ContactId,
      name,
      description,
      startDate: fmt(startDate),
      endDate: fmt(endDate),
      ...(fileName && base64File && { fileName, base64File }),
      subGoals: subGoals.map(s => ({
        title: s.title,
        description: s.description,
        startDate: fmt(s.startDate),
        endDate: fmt(s.endDate),
      })),
      applicableTo,
      audienceNameforfac: audienceName.join(','), // renamed to match expected field
    };
    
    // If it's a student, remove facultyID and use goaltype instead:
    if (isStudent) {
      delete payload.facultyID;
      delete payload.applicableTo;
      delete payload.audienceNameforfac;
      payload.goaltype = goaltype;
    }
    

    try {
      if (isStudent) await postStudentGoals(payload, userDetails.ContactId);
      else await postFacultyGoals(payload);
      showSnackbar('Submitted successfully!', 'success');
      setTimeout(() => navigate(-1), 2000);
    } catch {
      showSnackbar('Failed to submit', 'error');
    }
  };

  const filteredAudience = audienceOptions.filter(o =>
    o.label.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ bgcolor: '#f5f6f8', minHeight: '100vh', overflow: 'auto',  p: 3 }}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={10}>
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, p: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
              
              {/* Step 1 */}
              {step === 1 && (
                <div style={{ overflowY: 'auto', maxHeight: '81vh' }}>
                  <Typography variant="h5" style={{textAlign: 'left', marginTop :'20px', marginBottom: '20px'}}> Create your Goal</Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField label="Name / Title" fullWidth value={name} onChange={e => setName(e.target.value)} />
                    </Grid>
                    {isStudent && (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Goal Type</InputLabel>
                          <Select value={goaltype} onChange={e => setGoalType(e.target.value)}>
                            <MenuItem value="Academic">Academic</MenuItem>
                            <MenuItem value="Personal">Personal</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    {isStudent && (
                      <Grid item xs={12} md={6}>
                        <Button variant="outlined" component="label" fullWidth startIcon={<UploadFileIcon />}>
                          Upload<input type="file" hidden onChange={handleFileChange} />
                        </Button>
                        {fileName && <Typography mt={1}>{fileName}</Typography>}
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TextField label="Description" fullWidth multiline rows={4} value={description} onChange={e => setDescription(e.target.value)} />
                    </Grid>
                    {!isStudent && (
                      <>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Applicable to</InputLabel>
                            <Select value={applicableTo} onChange={e => setApplicableTo(e.target.value)}>
                              <MenuItem value="Individual">Individual</MenuItem>
                              <MenuItem value="Grade">Grade</MenuItem>
                              <MenuItem value="Subject">Subject</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Select Audience</InputLabel>
                            <Select
                              multiple
                              value={audienceName}
                              onChange={e => {
                                const val = e.target.value;
                                setAudienceName(typeof val === 'string' ? val.split(',') : val);
                              }}
                              input={<OutlinedInput label="Select Audience" />}
                              renderValue={sel =>
                                (sel as string[])
                                  .map(v => audienceOptions.find(o => o.value === v)?.label || v)
                                  .join(', ')
                              }
                              MenuProps={{
                                PaperProps: { style: { maxHeight: 300, width: 300 } },
                              }}
                            >
                              <MenuItem disabled>
                                <TextField
                                  autoFocus
                                  placeholder="Search..."
                                  fullWidth
                                  variant="standard"
                                  value={searchText}
                                  onChange={e => setSearchText(e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                  onMouseDown={e => e.stopPropagation()}
                                  InputProps={{ disableUnderline: true }}
                                />
                              </MenuItem>
                              {filteredAudience.length === 0
                                ? <MenuItem disabled>No options available</MenuItem>
                                : filteredAudience.map(opt => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    <Checkbox checked={audienceName.includes(opt.value)} />
                                    <ListItemText primary={opt.label} />
                                  </MenuItem>
                                ))
                              }
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} md={6}>
                      <DatePicker label="Start Date" value={startDate} onChange={d => setStartDate(d)} slotProps={{ textField: { fullWidth: true } }} format="dd/MM/yy" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DatePicker label="End Date" value={endDate} onChange={d => setEndDate(d)} slotProps={{ textField: { fullWidth: true } }} format="dd/MM/yy" />
                    </Grid>
                    {!isStudent && (
                      <Grid item xs={12} md={6}>
                        <Button variant="outlined" component="label" fullWidth startIcon={<UploadFileIcon />}>
                          Upload<input type="file" hidden onChange={handleFileChange} />
                        </Button>
                        {fileName && <Typography mt={1}>{fileName}</Typography>}
                      </Grid>
                    )}
                  </Grid>
                  <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                    <Button variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
                    <Button variant="contained" sx={{ bgcolor: '#2E4BCF', '&:hover': { bgcolor: '#263E9F' } }} onClick={() => setStep(2)}>
                      Continue
                    </Button>
                  </Box>
                </div>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <div style={{ overflowY: 'auto'}}>
                  <Typography variant="h5" style={{textAlign: 'left'}}>Create SubGoal</Typography>
                  <div style={{ height: '450px', overflowY: 'auto' }}>
  {subGoals.map((s, i) => (
    <Box key={i} sx={{ mb: 3 }}>
      <Typography variant="h5" gutterBottom style={{textAlign :'left', marginTop :'10px', marginBottom: '20px',fontWeight: '500'}}>
        Sub-Goal #{i + 1}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="SubGoal Title"
            fullWidth
            value={s.title}
            onChange={e => handleSubChange(i, 'title', e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="SubGoal Description"
            fullWidth
            multiline
            rows={4}
            value={s.description}
            onChange={e => handleSubChange(i, 'description', e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            label="Start Date"
            value={s.startDate}
            onChange={d => handleSubChange(i, 'startDate', d)}
            slotProps={{ textField: { fullWidth: true } }}
            format="dd/MM/yy"
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            label="End Date"
            value={s.endDate}
            onChange={d => handleSubChange(i, 'endDate', d)}
            slotProps={{ textField: { fullWidth: true } }}
            format="dd/MM/yy"
          />
        </Grid>
      </Grid>
    </Box>
  ))}
</div>

<Box sx={{ mt: 2, width: '200px' }}>
  <Button 
    onClick={addSub}
    variant="contained"
    fullWidth
    sx={{
      bgcolor: '#2E4BCF',
      '&:hover': { bgcolor: '#263E9F' }
    }}
  >
    + Add SubGoal
  </Button>
</Box>
                  <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                    <Button variant="outlined" onClick={() => setStep(1)}>Back</Button>
                    <Button variant="contained" sx={{ bgcolor: '#2E4BCF', '&:hover': { bgcolor: '#263E9F' } }} onClick={handleSubmit}>
                      Submit
                    </Button>
                  </Box>
                </div>
              )}

              <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>{snackbarMessage}</Alert>
              </Snackbar>

            </Box>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default AddGoalPage;
